import { IStatus } from "@treelab/common-logic";
import { IAttachment } from "@treelab/panda";

// 存放和 tableData 有关的 接口(interface) 和 枚举(enum)
// 目前从 treelab-client 移植
// 后续或使用 sdk 提供的定义

export enum ColumnType {
  CHECKBOX = "CHECKBOX", // 单选
  COLLABORATOR = "COLLABORATOR", // 成员
  CREATED_AT = "CREATED_AT", // 创建时间
  CREATED_BY = "CREATED_BY", // 创建人
  CURRENCY = "CURRENCY", // 货币
  DATETIME = "DATETIME", // 日期时间
  EMAIL = "EMAIL", // 邮箱
  FORMULA = "FORMULA", // 公式
  LONG_TEXT = "LONG_TEXT", // 长文本
  LOOKUP = "LOOKUP", // 查看
  MULTI_ATTACHMENT = "MULTI_ATTACHMENT", // 附件
  MULTI_SELECT = "MULTI_SELECT", // 多选
  NOOP = "NOOP",
  NUMBER = "NUMBER", // 数字
  PHONE = "PHONE", // 手机
  RATING = "RATING", // 评分
  RECORD_REFERENCE = "RECORD_REFERENCE", // 关联表
  ROLLUP = "ROLLUP", // 列公式
  SELECT = "SELECT", // 单选
  STATUS = "STATUS", // 状态列
  TEXT = "TEXT", // 文本
  UNIQUE_ID = "UNIQUE_ID",
  AUTO_NUMBER = "AUTO_NUMBER", //自增列
}

export enum FormulaResultType {
  DATE = "DATE",
  NUMBER = "NUMBER",
  TEXT = "TEXT",
}

export enum NodeUserType {
  NORMAL = "NORMAL",
  PUBLIC = "PUBLIC",
  SHARE = "SHARE",
  REMOVED = "REMOVED",
  INACTIVE = "INACTIVE",
}
export enum PermissionDeptRoles {
  OWNER = "OWNER",
  ADMIN = "ADMIN",
  MEMBER = "MEMBER",
  GUEST = "GUEST",
}

// export declare enum DateFormat {
export enum DateFormat {
  LOCAL = "LOCAL",
  FRIENDLY = "FRIENDLY",
  SLASH = "SLASH",
  ISO = "ISO",
}
export enum TimeFormat {
  TWELVE_HOUR = "TWELVE_HOUR",
  TWOFOUR_HOUR = "TWOFOUR_HOUR",
}

export enum NumberFormat {
  CURRENCY = "CURRENCY", // 货币
  DECIMAL = "DECIMAL", // 普通数字格式
  PERCENTAGE = "PERCENTAGE", // 百分比
  DECIMAL_WITH_COMMAS = "DECIMAL_WITH_COMMAS", // 千分位
}

export interface ITableColumn<T extends ColumnType = ColumnType> {
  id: string;
  name: string;
  type: ColumnType;
  typeOptions: ITypeOptions<T>;
}

export interface ITableRowData {
  readonly id: string;
  readonly cells: { [columnId: string]: unknown | undefined } | null;
}

export interface IViewRowData {
  readonly id: string;
  readonly order: number;
}

export interface IViewColumnData {
  readonly id: string;
  readonly isHidden: boolean | null;
  readonly isHiddenInKanban: boolean | null;
  readonly order: number;
  readonly width: number;
}

export enum ViewType {
  FORM = "FORM",
  GRID = "GRID",
  KANBAN = "KANBAN",
}

export interface ITableView {
  id: string;
  rows: IViewRowData[] | null;
  columns: IViewColumnData[] | null;
  type: ViewType;
}

export interface ITableData {
  id: string;
  columns: ITableColumn[];
  rows: ITableRowData[];
  views: ITableView[] | null;
}

export interface IUser {
  id: string;
  nickName: string;
  smallImage: string;
  individualGroupId: string;
}
export interface IWrokspaceData {
  id: string;
  name: string;
  users: { byId: Record<string, IUser>; allIds?: string[] };
  nodes: { byId: Record<string, INode>; allIds?: string[] };
}

export enum NodeType {
  FOLDER = "FOLDER",
  TABLE = "TABLE",
  VIEW = "VIEW",
  DASHBOARD = "DASHBOARD",
}

export enum PermissionNodeRoles {
  ADMIN = "ADMIN",
  EDITOR = "EDITOR",
  COMMENTER = "COMMENTER",
  VIEWER = "VIEWER",
}
export interface INode<T extends NodeType = NodeType> {
  readonly id: string;
  name: string;
  icon?: string;
  type: T;
  parentId?: string;
  childNodeIds: string[];
  hideParent?: boolean;
  hasLoadedChildNodes?: boolean;
  expanded?: boolean;
  description?: string;
  cover?: string;
  role: PermissionNodeRoles;
  share?: { shareId: string; saveable?: boolean };
  color?: string;
  order: number;
}

export type ITypeOptions<T extends ColumnType = ColumnType> = T extends ColumnType.NUMBER
  ? INumberTypeOptions
  : T extends ColumnType.CURRENCY
  ? ICurrencyTypeOptions
  : T extends ColumnType.DATETIME
  ? IDateTimeTypeOptions
  : T extends ColumnType.STATUS
  ? IStatusTypeOptions
  : T extends ColumnType.SELECT
  ? ISelectTypeOptions
  : T extends ColumnType.MULTI_SELECT
  ? ISelectTypeOptions
  : T extends ColumnType.RECORD_REFERENCE
  ? IRecordReferenceTypeOptions
  : T extends ColumnType.LOOKUP
  ? ILookupTypeOptions
  : T extends ColumnType.ROLLUP
  ? IRollupTypeOptions
  : T extends ColumnType.UNIQUE_ID
  ? IUniqueIdOptions
  : T extends ColumnType.RATING
  ? IRatingOptions
  : T extends ColumnType.FORMULA
  ? IFormulaOptions
  : T extends ColumnType.COLLABORATOR
  ? ICollaboratorTypeOptions
  : T extends ColumnType.CREATED_AT
  ? ICreatedAtTypeOptions
  : T extends ColumnType.CHECKBOX
  ? IGenericTypeOptions<ColumnType.CHECKBOX>
  : T extends ColumnType.LONG_TEXT
  ? IGenericTypeOptions<ColumnType.LONG_TEXT>
  : T extends ColumnType.MULTI_ATTACHMENT
  ? IGenericTypeOptions<ColumnType.MULTI_ATTACHMENT>
  : T extends ColumnType.NOOP
  ? IGenericTypeOptions<ColumnType.NOOP>
  : T extends ColumnType.TEXT
  ? IGenericTypeOptions<ColumnType.TEXT>
  : T extends ColumnType.PHONE
  ? IGenericTypeOptions<ColumnType.PHONE>
  : T extends ColumnType.EMAIL
  ? IGenericTypeOptions<ColumnType.EMAIL>
  : T extends ColumnType.AUTO_NUMBER
  ? IGenericTypeOptions<ColumnType.AUTO_NUMBER>
  : T extends ColumnType.CREATED_BY
  ? IGenericTypeOptions<ColumnType.AUTO_NUMBER>
  : never;
export interface INumberTypeOptions {
  type: ColumnType.NUMBER | ColumnType.LOOKUP;
  defaultNumber?: number;
  numberFormat: NumberFormat;
  precision: number;
}

export interface ICurrencyTypeOptions {
  type: ColumnType.CURRENCY | ColumnType.LOOKUP;
  defaultNumber?: number;
  precision: number;
  currencyFormat: string;
}

export interface IDateTimeTypeOptions {
  type: ColumnType.DATETIME | ColumnType.LOOKUP;
  dateFormat: DateFormat;
  includeTime: boolean;
  timeFormat: TimeFormat;
  useGMT: boolean;
  useCurrentDateTime?: boolean;
}

interface IRecordReferenceTypeOptions {
  type: ColumnType.RECORD_REFERENCE | ColumnType.LOOKUP;
  foreignWorkspaceId?: string;
  foreignCoreId: string;
  foreignTableId: string;
  foreignViewId?: string;
  useMultiple?: boolean;
  symmetricColumnId?: string;
}

export interface ILookupTypeOptions {
  type: ColumnType.LOOKUP;
  recordReferenceColumnId: string;
  foreignLookupColumnId: string;
  errorCode?: string;

  lookupColumnType: Exclude<ColumnType, ColumnType.NOOP | ColumnType.LOOKUP>;
  lookupTypeOptions: Exclude<ITypeOptions, ColumnType.NOOP | ColumnType.LOOKUP>;
}

interface IRollupTypeOptions {
  type: ColumnType.ROLLUP | ColumnType.LOOKUP;
  formula: string;
  recordReferenceColumnId?: string;
  errorCode?: string;
  resultType: FormulaResultType;
  numberFormat?: NumberFormat;
  precision?: number;
  currencyFormat?: string;
  dateFormat?: DateFormat;
  timeFormat?: TimeFormat;
  includeTime?: boolean;
  useGMT?: boolean;
  useCurrentDateTime?: boolean;
}

export interface IRatingOptions {
  type: ColumnType.RATING | ColumnType.LOOKUP;
  ratingMax: number;
}

interface IUniqueIdOptions {
  type: ColumnType.UNIQUE_ID | ColumnType.LOOKUP;
  // idFormat?: IDFormat;
}

export interface IFormulaOptions {
  type: ColumnType.FORMULA | ColumnType.LOOKUP;
  formula: string;
  errorCode?: string;
  resultType: FormulaResultType;
  numberFormat?: NumberFormat;
  precision?: number;
  currencyFormat?: string;
  dateFormat?: DateFormat;
  timeFormat?: TimeFormat;
  includeTime?: boolean;
  useGMT?: boolean;
  useCurrentDateTime?: boolean;
}
export interface IStatusTypeOptions {
  type: ColumnType.STATUS | ColumnType.LOOKUP;
  statuses: IStatus[];
  statusColumnId?: string;
  assigneeColumnId?: string;
  endTimeColumnId?: string;
}
export interface ISelectTypeOptions {
  type: ColumnType.SELECT | ColumnType.MULTI_SELECT | ColumnType.LOOKUP;
  options: ISelect[];
}
export interface ISelect {
  color: string;
  name: string;
  optionId: string;
}

interface ICollaboratorTypeOptions {
  type: ColumnType.COLLABORATOR | ColumnType.LOOKUP;
  useMultiple: boolean;
  useGroup: boolean;
  useNotify: boolean;
  groups: string[];
}

interface ICreatedAtTypeOptions {
  type: ColumnType.CREATED_AT | ColumnType.LOOKUP;
  dateFormat: DateFormat;
  includeTime: boolean;
  timeFormat: TimeFormat;
  useGMT: boolean;
}

interface IGenericTypeOptions<T extends ColumnType = ColumnType> {
  type: T | ColumnType.LOOKUP;
}

export interface IAdaptedAttachment extends IAttachment {
  order?: number;
}
